// src/features/chat/CreateTaskFromMessage.tsx
// Turn a chat message into a task without leaving the conversation.
//
// The message carries the why: its text seeds the title and description, and
// the description keeps a back-link to the exact message so the task never
// loses the thread it was born from. When the conversation itself belongs to
// something (a project, a team), the task is created under that same parent,
// because "make this a task" almost always means "track it where this chat
// lives".
//
// The board column picker lists the workspace's real columns — custom
// TaskStatusDefinitions when they exist, the base status enum otherwise —
// through the same buildBoardColumns the board itself uses. CreateTaskRequest
// has no status field, so a non-default column is applied with a follow-up
// ChangeTaskStatus after create; both that call and the message↔task resource
// link are best-effort: the task existing is the success condition, everything
// else is a warning, not a failure.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useTask, useTaskStatusDefinition } from '@react/hooks';

import { FormDialog } from '@/components/form';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { PrioritySelect } from '@/components/PrioritySelect';
import {
  DEFAULT_BOARD_STATUSES,
  buildBoardColumns,
  buildCreatePayload,
  buildStatusChangeRequest,
  getEntityId,
} from '@/features/task/logic';
import { entityToUrn, urnToEntity } from '@/features/resources/logic';
import { errMsg } from '@/features/task/panelHelpers';
import { LinkResources } from '@core/api/endpoints/resource/link_resources';

/** First line of the message, trimmed, capped at the task-title length. */
const TITLE_MAX = 120;

function titleFromContent(content: string): string {
  const firstLine = (content || '').split('\n')[0].trim();
  return firstLine.length > TITLE_MAX ? firstLine.slice(0, TITLE_MAX).trimEnd() : firstLine;
}

export function CreateTaskFromMessage(props: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  message: any; // chat Message
  conversationId?: string;
  parentEntity?: { id: string; type_name: string } | null;
}) {
  const { open, onOpenChange, message, conversationId, parentEntity } = props;
  const { t } = useI18n();
  const store = useTask();
  const statusDefStore = useTaskStatusDefinition();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [columnId, setColumnId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [priority, setPriority] = useState('TASK_PRIORITY_MEDIUM');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  // Custom status definitions arrive async; the picker re-lists itself.
  useEffect(() => {
    if (open) statusDefStore.ListStatusDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const definitions = statusDefStore.items ?? [];
  const columns = useMemo(() => buildBoardColumns(definitions, []), [definitions]);

  // Reset and prefill whenever a (new) message is opened.
  useEffect(() => {
    if (!open) return;
    const content: string = message?.content || '';
    setTitle(titleFromContent(content));
    const quote = content
      .split('\n')
      .map((line: string) => `> ${line}`)
      .join('\n');
    const messageId = message?.entity?.id;
    const backLink =
      conversationId && messageId ? `\n\n${t('chat.createTask.backLink')}: /chats/${conversationId}?m=${messageId}` : '';
    setDescription(`${quote}${backLink}`.trim());
    setColumnId(columns[0]?.id || DEFAULT_BOARD_STATUSES[1]);
    setAssignee('');
    setAssigneeName('');
    setPriority('TASK_PRIORITY_MEDIUM');
    setError(null);
    setCreatedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, message]);

  const close = () => onOpenChange(false);

  const handleSubmit = async () => {
    if (!title.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const payload = buildCreatePayload({
        title,
        description,
        type: 'TASK_TYPE_TASK',
        priority,
        assignee,
        assignee_name: assigneeName,
        due_date: '',
        parent_id: parentEntity?.id,
        parent_type: parentEntity?.type_name,
      });

      const created = await store.CreateTask(payload);
      const taskId = getEntityId(created);

      // The create request carries no status; a non-default column is a
      // follow-up ChangeTaskStatus. Best-effort — the task exists either way.
      const defaultColumn = columns[0]?.id;
      if (taskId && columnId && columnId !== defaultColumn) {
        try {
          await store.ChangeTaskStatus(taskId, buildStatusChangeRequest(taskId, columnId, definitions));
        } catch (e) {
          console.warn('[CreateTaskFromMessage] status change failed', e);
        }
      }

      // Link message ↔ task so the task shows where it came from. Never
      // blocks: a failed link leaves a perfectly good task behind.
      const messageUrn = entityToUrn(message?.entity);
      const source = urnToEntity(messageUrn);
      if (taskId && source) {
        try {
          await LinkResources({
            source: source as any,
            target: { type_id: 'task', type_name: 'task', id: taskId } as any,
            relation_type: 'RELATION_TYPE_REFERENCES',
            relation_label: 'task_created_from',
          });
        } catch (e) {
          console.warn('[CreateTaskFromMessage] resource link failed', e);
        }
      }

      setCreatedId(taskId || null);
    } catch (e) {
      setError(errMsg(e, t('chat.createTask.error')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const columnOptions = columns.map((col) => ({
    value: col.id,
    label: col.label || t(col.labelKey || 'common.unknown'),
  }));

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={handleSubmit}
      submitLabel={t('chat.createTask.submit')}
      canSubmit={!!title.trim() && !createdId}
      isSubmitting={isSubmitting}
      dirty={!createdId && title.trim().length > 0}
      aria-label={t('chat.createTask.title')}
    >
      <div className="flex flex-col gap-3 px-3.5 pb-3">
        <h2 className="text-sm font-semibold text-foreground">{t('chat.createTask.title')}</h2>

        {createdId ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2.5">
            <span className="text-sm text-foreground">{t('chat.createTask.success')}</span>
            <Link
              to={`/tasks/${createdId}`}
              onClick={close}
              className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('chat.createTask.openTask')}
            </Link>
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="ctf-title" className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('chat.createTask.fieldTitle')}
              </label>
              <input
                id="ctf-title"
                data-autofocus
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={TITLE_MAX}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="ctf-description" className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('chat.createTask.fieldDescription')}
              </label>
              <textarea
                id="ctf-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <Select
              value={columnId}
              onChange={(e: any) => setColumnId(e?.target ? e.target.value : e)}
              options={columnOptions}
              label={t('chat.createTask.boardColumn')}
              fullWidth
            />

            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('chat.createTask.assignee')}
              </span>
              <UserPicker
                value={assignee}
                onChange={(userId, displayName) => {
                  setAssignee(userId);
                  setAssigneeName(displayName);
                }}
              />
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('chat.createTask.priority')}
              </span>
              <PrioritySelect value={priority} onSelect={setPriority} />
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </FormDialog>
  );
}

export default observer(CreateTaskFromMessage);
